/**
 * WeightTrendSuggestionCard — kind-spezifischer Renderer für
 * 'weight_trend'-Suggestions des deterministischen Watchers.
 *
 * Zeigt:
 *   - Trend-Badge (stable/moderate/high)
 *   - Aktuelles Gewicht + Baseline + Verlust-% (30d / 180d)
 *   - kurze Rationale
 *   - "Verlauf öffnen" als primäre Aktion
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, TrendingDown, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import {
  isWeightTrendPayload,
  type AiSuggestion,
  type WeightTrendSuggestionPayload,
} from '@/lib/ai/types';

interface Props {
  suggestion: AiSuggestion;
}

const LEVEL_STYLE: Record<WeightTrendSuggestionPayload['trend_level'], string> = {
  stable:
    'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
  moderate:
    'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
  high: 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100',
};

const LEVEL_LABEL: Record<WeightTrendSuggestionPayload['trend_level'], string> = {
  stable: 'Stabil',
  moderate: 'Veränderung dokumentiert',
  high: 'Schwellenwert erreicht',
};

export const WeightTrendSuggestionCard: React.FC<Props> = ({ suggestion }) => {
  const navigate = useNavigate();
  const { resolve } = useAiCompanion();

  if (!isWeightTrendPayload(suggestion.payload)) return null;
  const p = suggestion.payload;

  const primary = p.suggested_actions[0];
  const secondary = p.suggested_actions.slice(1, 3);

  const handlePrimary = async () => {
    if (suggestion.patient_id) {
      navigate(`/patients/${suggestion.patient_id}?tab=klinik`);
    }
    await resolve(suggestion, 'accepted', primary?.kind ?? 'open_patient');
  };

  const handleDismiss = async () => {
    await resolve(suggestion, 'dismissed');
  };

  const fmtPct = (v: number | null) =>
    v === null ? null : `${v.toFixed(1).replace('.', ',')}%`;

  return (
    <article className="border-b p-4 last:border-b-0">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'rounded-md px-2 py-0.5 text-xs font-medium',
              LEVEL_STYLE[p.trend_level],
            )}
          >
            <TrendingDown className="mr-1 inline h-3 w-3" />
            {LEVEL_LABEL[p.trend_level]}
          </span>
          <span className="text-xs text-muted-foreground">Gewichtstrend</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Hinweis schließen"
          onClick={handleDismiss}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <dl className="mb-2 grid grid-cols-2 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Aktuell</dt>
        <dd className="text-foreground">
          {p.current_weight_kg.toFixed(1).replace('.', ',')} kg
        </dd>
        {p.baseline_weight_kg !== null && (
          <>
            <dt className="text-muted-foreground">Baseline</dt>
            <dd className="text-foreground">
              {p.baseline_weight_kg.toFixed(1).replace('.', ',')} kg
            </dd>
          </>
        )}
        {p.loss_pct_1m !== null && (
          <>
            <dt className="text-muted-foreground">Verlust 30 Tage</dt>
            <dd className="text-foreground">{fmtPct(p.loss_pct_1m)}</dd>
          </>
        )}
        {p.loss_pct_6m !== null && (
          <>
            <dt className="text-muted-foreground">Verlust 6 Monate</dt>
            <dd className="text-foreground">{fmtPct(p.loss_pct_6m)}</dd>
          </>
        )}
      </dl>

      <p className="mb-3 text-sm text-foreground">{p.rationale}</p>

      <div className="flex flex-wrap gap-2">
        {primary && (
          <Button size="sm" onClick={handlePrimary}>
            {primary.label}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
        {secondary.map((a, i) => (
          <Badge
            key={`${a.kind}-${i}`}
            variant="outline"
            className="text-xs font-normal"
          >
            {a.label}
          </Badge>
        ))}
      </div>
    </article>
  );
};
